// src/pages/sessions/components/LiveSession/hooks/useConnectionRecovery.js
import { useEffect, useRef } from 'react';
import { RoomEvent } from 'livekit-client';
import { toast } from 'sonner';
import sessionService from '@/services/sessionService';

// Cleanup utility for connection recovery
class ConnectionRecoveryCleanup {
  constructor() {
    this.timeouts = new Set();
    this.listeners = new Map();
    this.abortControllers = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  addListener(room, event, handler) {
    if (this.disposed || !room) return;
    const key = `${event}-${Date.now()}`;
    room.on(event, handler);
    this.listeners.set(key, { room, event, handler });
    return key;
  }

  createAbortController() {
    if (this.disposed) return null;
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  cleanup() {
    this.disposed = true;
    
    // Clear all timeouts
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
    
    // Remove all event listeners
    this.listeners.forEach(({ room, event, handler }) => {
      try {
        room.off(event, handler);
      } catch (e) {
        console.debug('Listener cleanup error:', e);
      }
    });
    this.listeners.clear();
    
    // Abort all fetch requests
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {}
    });
    this.abortControllers.clear();
  }
}

export function useConnectionRecovery({
  roomRef,
  roomConnected,
  isTeacherUser,
  sessionId,
  processRoomChange,
  applyAllSubscriptions,
}) {
  // Cleanup tracker
  const cleanupRef = useRef(new ConnectionRecoveryCleanup());
  const cleanup = cleanupRef.current;
  
  // Track mounted state
  const mountedRef = useRef(true);
  
  // Track listener key for cleanup
  const listenerKeyRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!roomRef.current || !roomConnected) return;
    
    const room = roomRef.current;
    
    const onReconnected = async () => {
      if (!mountedRef.current) return;
      
      console.log('Room reconnected, restoring state...');
      
      // Create abort controller for this recovery session
      const abortController = cleanup.createAbortController();
      
      // Minimal delay for LiveKit stabilization with cleanup
      await new Promise((resolve, reject) => {
        const timeoutId = cleanup.setTimeout(resolve, 100);
        
        // Handle abort
        if (abortController) {
          abortController.signal.addEventListener('abort', () => {
            reject(new DOMException('Recovery aborted', 'AbortError'));
          });
        }
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Recovery delay error:', err);
        }
      });
      
      if (!mountedRef.current) return;
      
      // Re-fetch current room assignment for students
      if (!isTeacherUser && sessionId) {
        try {
          const assignmentRes = await sessionService.getUserRoomAssignment(
            sessionId,
            { signal: abortController?.signal }
          );
          
          if (!mountedRef.current) return;
          
          if (assignmentRes?.success && assignmentRes?.data?.virtualRoomId) {
            const roomId = assignmentRes.data.virtualRoomId;
            console.log('Restored room assignment:', roomId);
            
            // Process room change with mounted check
            if (processRoomChange && mountedRef.current) {
              await processRoomChange(roomId);
            }
          }
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('Failed to restore room assignment:', e);
          }
        }
      }
      
      if (!mountedRef.current) return;
      
      // Reapply all subscriptions
      if (applyAllSubscriptions && mountedRef.current) {
        applyAllSubscriptions();
      }
      
      // Show success toast only if still mounted
      if (mountedRef.current) {
        toast.success('Reconnected to session');
      }
    };
    
    // Add listener with cleanup tracking
    listenerKeyRef.current = cleanup.addListener(room, RoomEvent.Reconnected, onReconnected);
    
    return () => {
      // Remove specific listener
      if (listenerKeyRef.current) {
        const listener = cleanup.listeners.get(listenerKeyRef.current);
        if (listener) {
          try {
            listener.room.off(listener.event, listener.handler);
          } catch (e) {
            console.debug('Error removing reconnect listener:', e);
          }
          cleanup.listeners.delete(listenerKeyRef.current);
        }
        listenerKeyRef.current = null;
      }
    };
  }, [
    roomConnected, 
    isTeacherUser, 
    sessionId, 
    processRoomChange, 
    applyAllSubscriptions,
    cleanup
  ]);
  
  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      
      // Cleanup all resources
      cleanup.cleanup();
    };
  }, [cleanup]);
}